import { useState, useCallback, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Landmark } from 'lucide-react';

import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { MasterFlash }         from '@/components/master/MasterFlash';
import { MasterAuditTab }      from '@/components/master/MasterAuditTab';
import { MasterConfirmDialogs } from '@/components/master/MasterConfirmDialogs';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import { Badge, Card, Input, Tabs, Toggle } from '@/components/ui';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useFlashMessage }     from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { useMasterRecord, masterListFetcher } from '@/hooks/useMasterRecord';
import { Permissions }         from '@/constants/permissions';
import { bankApi }             from '@/api/bank.api';
import type { BankDto, BankListDto } from '@/types/bank.types';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  bankCode:  z.string().min(1, 'Bank code is required').max(20),
  bankName:  z.string().min(1, 'Bank name is required').max(200),
  // ISO 9362: eight characters, or eleven with the branch suffix. The server enforces this;
  // the form used to accept anything up to 20 and let the save fail instead.
  swiftCode: z.string().trim()
               .refine(v => v === '' || v.length === 8 || v.length === 11,
                       'SWIFT/BIC code must be 8 or 11 characters')
               .nullable().optional(),
  country:   z.string().max(100).nullable().optional(),
  isActive:  z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  bankCode: '', bankName: '', swiftCode: null, country: null, isActive: true,
};

const TABS = ['General', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<BankListDto>[] = [
  { key: 'bankCode',  label: 'Code',       className: 'w-20 font-mono' },
  { key: 'bankName',  label: 'Bank Name' },
  { key: 'swiftCode', label: 'SWIFT',      className: 'w-24 font-mono', render: r => r.swiftCode ?? '—' },
  { key: 'country',   label: 'Country',    className: 'w-24',           render: r => r.country ?? '—' },
  { key: 'isActive',  label: 'Status',     className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

function dtoToForm(dto: BankDto): FormValues {
  return {
    bankCode:  dto.bankCode,
    bankName:  dto.bankName,
    swiftCode: dto.swiftCode ?? null,
    country:   dto.country ?? null,
    isActive:  dto.isActive,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const BankPage = () => {
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.MST_BANK_VIEW);
  const canCreate = hasPermission(Permissions.MST_BANK_CREATE);
  const canEdit   = hasPermission(Permissions.MST_BANK_EDIT);
  const canDelete = hasPermission(Permissions.MST_BANK_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const [codeInput,      setCodeInput]      = useState('');
  const [showNewConfirm, setShowNewConfirm] = useState<string | null>(null);

  const {
    register, handleSubmit, control, reset, watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  const isActive = watch('isActive');

  const master = useMasterRecord<BankDto, Tab, FormValues>({
    screen, flash, firstTab: 'General', focusRef: codeInputRef,
    clearForm:   () => reset(EMPTY_FORM),
    clearScreen: () => setCodeInput(''),
    onDelete: {
      remove:   id => bankApi.remove(id),
      describe: r => `Bank '${r.bankCode}'`,
    },
  });

  const {
    record, setRecord, activeTab, setActiveTab, saving, setSaving, lookupBusy, setLookupBusy,
    printOpen, setPrintOpen, searchOpen, setSearchOpen,
    pendingSave, setPendingSave, showSaveConfirm, setShowSaveConfirm,
    showDeleteConfirm, setShowDeleteConfirm, resetToInitial, executeDelete, beginSave,
  } = master;

  const loadRecord = useCallback((dto: BankDto) => {
    setRecord(dto); reset(dtoToForm(dto)); setCodeInput(dto.bankCode);
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen, setRecord, setActiveTab]);

  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    if (!canView) { flash('error', 'You do not have permission to view banks.'); return; }
    setLookupBusy(true);
    try {
      const res = await bankApi.getByCode(code);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) { flash('error', `Bank '${code}' was not found.`); return; }
        setShowNewConfirm(code);
      } else { flash('error', 'Lookup failed. Please try again.'); }
    } finally { setLookupBusy(false); }
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm; setShowNewConfirm(null); if (!code) return;
    reset({ ...EMPTY_FORM, bankCode: code }); setCodeInput(code);
    setRecord(null); screen.setMode('new'); setActiveTab('General');
  };

  const handleSaveClick = () => {
    // The second argument runs when the form is invalid. Without it a failed validation did
    // nothing at all -- no save, no message, no focus -- so the button looked broken rather than
    // the form looking wrong.
    handleSubmit(beginSave, (formErrors) => {
      const first = Object.values(formErrors)[0]?.message;
      flash('error', first ? String(first) : 'Please correct the highlighted fields.');
    })();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await bankApi.create({
          bankCode:  d.bankCode.trim().toUpperCase(),
          bankName:  d.bankName.trim(),
          swiftCode: n(d.swiftCode),
          country:   n(d.country),
        });
        flash('success', 'Bank created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await bankApi.update(record.id, {
          bankName:  d.bankName.trim(),
          swiftCode: n(d.swiftCode),
          country:   n(d.country),
          isActive:  d.isActive,
        });
        flash('success', 'Bank updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };

  const fetchBanks = useMemo(() => masterListFetcher(bankApi.getList), []);

  const handleSearchSelect = (item: BankListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    bankApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load bank.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={Landmark} title="Bank Maintenance"
        subtitle="Manage bank records" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} {...master.toolbar}
        saving={saving} loading={lookupBusy} onSave={handleSaveClick}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Bank Code"
        summary={record && (<>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.bankName}</span>
          {record.swiftCode && <span className="font-mono text-xs text-gray-400">{record.swiftCode}</span>}
          {!record.isActive && (
            <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
          )}
        </>)}
        hint={screen.isInitial ? "Enter a bank code and press Enter, or browse." : undefined}
      >
        <MasterCodeLookup
          inputRef={codeInputRef}
          value={codeInput}
          onChange={setCodeInput}
          onSubmit={handleCodeLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Search banks"
          placeholder="Type code + Enter…"
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">
            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Bank Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Bank Code" {...register('bankCode')} disabled
                    placeholder="— enter via code field above —" className="font-mono uppercase" />
                  <Input label="Bank Name *" {...register('bankName')} error={errors.bankName?.message}
                    disabled={fieldsDisabled} placeholder="e.g. People's Bank" />
                  <Input label="SWIFT Code" {...register('swiftCode')} disabled={fieldsDisabled}
                    maxLength={20} placeholder="e.g. PSBKLKLX" className="font-mono uppercase" />
                  <Input label="Country" {...register('country')} disabled={fieldsDisabled}
                    placeholder="e.g. Sri Lanka" />
                  {(screen.isEdit || screen.isView) && (
                    <div className="sm:col-span-2">
                      <Controller name="isActive" control={control} render={({ field }) => (
                        <Toggle label={isActive ? 'Active' : 'Inactive'} checked={field.value}
                          onChange={field.onChange} disabled={fieldsDisabled} />
                      )} />
                    </div>
                  )}
                </div>
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      <MasterSearchModal<BankListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Banks" columns={SEARCH_COLUMNS}
        fetchData={fetchBanks} onSelect={handleSearchSelect}
      />

      <MasterConfirmDialogs
        entity="Bank"
        screen={screen}
        record={record}
        describe={r => `${r.bankCode} — ${r.bankName}`}
        saving={saving}
        create={{
          label: showNewConfirm,
          onConfirm: handleConfirmCreateNew,
          onCancel: () => setShowNewConfirm(null),
        }}
        saveOpen={showSaveConfirm}
        onSaveConfirm={executeSave}
        onSaveCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
        deleteOpen={showDeleteConfirm}
        onDeleteConfirm={executeDelete}
        onDeleteCancel={() => setShowDeleteConfirm(false)}
      />

      {record && (
        <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Bank Report">
          <ReportHeader reportTitle="Bank Master Report"
            printedBy={user?.fullName ?? user?.username} printedAt={new Date()}
            meta={[{ label: 'Bank Code', value: record.bankCode }]}
          />
          <ReportSection title="Bank Details" fields={[
            { label: 'Bank Code',  value: record.bankCode },
            { label: 'Bank Name',  value: record.bankName, wide: true },
            { label: 'SWIFT Code', value: record.swiftCode },
            { label: 'Country',    value: record.country },
            { label: 'Status',     value: record.isActive, display: record.isActive ? 'Active' : 'Inactive' },
          ]} />
          <ReportSection title="Audit Information" fields={[
            { label: 'Created By',    value: record.createdByName ?? record.createdBy },
            { label: 'Created Date',  value: formatDateTime(record.createdDate) },
            { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
            { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
          ]} />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
